import {
  Fragment,
  memo,
  useEffect,
  useEffectEvent,
  useLayoutEffect,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
  type Ref,
} from "react";
import { useNavigate } from "@tanstack/react-router";
import {
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import { LinkifyIt } from "linkify-it";
import ArrowDown from "~icons/ph/arrow-down";
import ArrowLeft from "~icons/ph/arrow-left";
import MessageCircle from "~icons/ph/chat-circle";
import Clock from "~icons/ph/clock";
import Phone from "~icons/ph/phone";
import Send from "~icons/ph/paper-plane-tilt";
import Video from "~icons/ph/video-camera";
import WarningCircle from "~icons/ph/warning-circle";
import type { AuthenticatedSnapshot, Person } from "../../shared/types";
import { useAppLocation } from "../app/location";
import { useShell } from "../app/shell";
import {
  useDiscardMessage,
  useLoadOlderMessages,
  useMarkRead,
  useSendMessage,
  useSubmitting,
} from "../data/mutations";
import {
  messagesOptions,
  type Conversation,
  type OutgoingMessage,
} from "../data/queries";
import {
  ActivityStatus,
  FriendSearch,
  PersonMenu,
  ProfileAvatar,
} from "../ui/components";
import { day, number, t, time } from "../i18n";
import { useNow } from "../ui/use-now";
import { usePageVisible } from "../ui/use-page-visible";

type Messages = UseQueryResult<Conversation>;

const sameDay = (a: number, b: number) =>
  new Date(a).toDateString() === new Date(b).toDateString();

// Web and mail addresses only; a message never becomes any other kind of link.
const linkify = new LinkifyIt({ fuzzyLink: true })
  .add("ftp:", null)
  .add("//", null);

const MessageText = memo(function MessageText({ body }: { body: string }) {
  const parts: ReactNode[] = [];
  let end = 0;
  for (const link of linkify.match(body) ?? []) {
    parts.push(
      body.slice(end, link.index),
      <a
        key={link.index}
        href={link.url}
        target="_blank"
        rel="noopener noreferrer"
        dir="auto"
      >
        {link.text}
      </a>,
    );
    end = link.lastIndex;
  }
  parts.push(body.slice(end));
  return parts;
});

function MessageList({
  messages,
  meId,
  peerId,
  peerName,
  readThrough,
  onRetry,
  onDiscard,
  onReachLatest,
}: {
  messages: Messages;
  meId: string;
  peerId: string;
  peerName: string;
  readThrough: number;
  onRetry: (message: OutgoingMessage) => void;
  onDiscard: (message: OutgoingMessage) => void;
  onReachLatest: () => void;
}) {
  const list = useRef<HTMLDivElement>(null);
  // Follow new messages only while the reader is at the latest one.
  const following = useRef(true);
  // Reading further back, a new message from them waits behind a pill.
  const [behind, setBehind] = useState(false);
  const latestIncoming = useRef<string>(undefined);
  const latest = useRef<string>(undefined);
  const earliest = useRef<string>(undefined);
  // Older messages arrive above the reader, who stays this far from the end.
  const fromEnd = useRef(0);
  const older = useLoadOlderMessages(meId, peerId);
  const loaded = messages.data?.messages;
  // One marker, on the newest message they have read. Their reply says it already.
  const replied = loaded?.findLastIndex((m) => m.sender !== meId) ?? -1;
  const readId = loaded?.findLast(
    (m, index) => index > replied && !m.outgoing && m.created_at <= readThrough,
  )?.id;
  useLayoutEffect(() => {
    const element = list.current;
    if (!element) return;
    const first = loaded?.[0]?.id;
    const last = loaded?.at(-1);
    const incoming = loaded?.findLast((m) => m.sender !== meId)?.id;
    const readBack =
      first !== earliest.current &&
      loaded?.some((m) => m.id === earliest.current);
    if (readBack) element.scrollTop = element.scrollHeight - fromEnd.current;
    else if (
      following.current ||
      (last?.sender === meId && last.id !== latest.current)
    )
      element.scrollTop = element.scrollHeight;
    else if (incoming !== latestIncoming.current) setBehind(true);
    latestIncoming.current = incoming;
    latest.current = last?.id;
    earliest.current = first;
    fromEnd.current = element.scrollHeight - element.scrollTop;
    // The marker adds a line, which must not push the latest message out of view.
  }, [loaded, meId, readId]);
  return (
    <div className="chat-log">
      <div
        className="chat-messages"
        role="log"
        ref={list}
        onScroll={(e) => {
          const { scrollHeight, scrollTop, clientHeight } = e.currentTarget;
          following.current = scrollHeight - scrollTop - clientHeight < 80;
          if (following.current) setBehind(false);
          fromEnd.current = scrollHeight - scrollTop;
          if (
            scrollTop < clientHeight &&
            messages.data?.hasOlder &&
            !older.isPending &&
            !older.isError
          )
            older.mutate();
        }}
      >
        {messages.data?.hasOlder && (
          <div className="older-messages">
            {older.isError && <p role="alert">{t(older.error.message)}</p>}
            <button
              className="quiet-button"
              onClick={() => {
                if (!older.isPending) older.mutate();
              }}
            >
              {older.isPending
                ? t("جارٍ تحميل الرسائل…")
                : older.isError
                  ? t("إعادة المحاولة")
                  : t("عرض الرسائل الأقدم")}
            </button>
          </div>
        )}
        {messages.isPending && (
          <div className="inline-empty" role="status">
            <div className="message-skeleton" aria-hidden="true">
              <i />
              <i />
              <i />
            </div>
          </div>
        )}
        {messages.isError && (
          <div className="inline-empty" role="alert">
            <p>{t(messages.error.message)}</p>
            <button
              className="quiet-button"
              onClick={() => void messages.refetch()}
            >
              {t("إعادة المحاولة")}
            </button>
          </div>
        )}
        {!messages.isPending && !messages.isError && !loaded?.length && (
          <div className="inline-empty">
            <MessageCircle width={30} height={30} />
            <p>{t("لا توجد رسائل بعد.")}</p>
          </div>
        )}
        {loaded?.map((m, index, all) => {
          const previous = all[index - 1];
          const newDay =
            !previous || !sameDay(previous.created_at, m.created_at);
          const groupStart =
            newDay ||
            previous.sender !== m.sender ||
            m.created_at - previous.created_at >= 5 * 60_000;
          const mine = m.sender === meId;
          const sender = mine ? t("أنت") : peerName;
          const outgoing = { id: m.id, peer: m.recipient, body: m.body };
          return (
            <Fragment key={m.id}>
              {newDay && <p className="message-day">{day(m.created_at)}</p>}
              <div
                className={`message ${mine ? "mine" : ""} ${groupStart ? "group-start" : ""}`}
              >
                <span className="sr-only" dir="auto">
                  {sender}
                </span>
                <div className="message-bubble">
                  <p dir="auto">
                    <MessageText body={m.body} />
                  </p>
                  <time dateTime={new Date(m.created_at).toISOString()}>
                    {time(m.created_at)}
                  </time>
                  {m.outgoing === "sending" && (
                    <Clock
                      className="message-waiting"
                      width={14}
                      height={14}
                      aria-hidden="true"
                    />
                  )}
                </div>
                {m.id === readId && (
                  <p className="message-read">{t("مقروءة")}</p>
                )}
                {m.outgoing === "failed" && (
                  <div className="message-failed" role="alert">
                    <span>
                      <WarningCircle
                        width={14}
                        height={14}
                        aria-hidden="true"
                      />
                      {t("لم تُرسل")}
                    </span>
                    <button
                      className="quiet-button"
                      onClick={() => onRetry(outgoing)}
                    >
                      {t("إعادة المحاولة")}
                    </button>
                    <button
                      className="quiet-button"
                      onClick={() => onDiscard(outgoing)}
                    >
                      {t("إزالة")}
                    </button>
                  </div>
                )}
              </div>
            </Fragment>
          );
        })}
      </div>
      {behind && (
        <button
          className="button secondary small new-message"
          onClick={() => {
            list.current?.scrollTo({ top: list.current.scrollHeight });
            setBehind(false);
            onReachLatest();
          }}
        >
          <ArrowDown width={14} height={14} aria-hidden="true" />
          {t("رسالة جديدة")}
        </button>
      )}
    </div>
  );
}

function MessageForm({
  peer,
  composerRef,
  onSend,
}: {
  peer: string;
  composerRef: Ref<HTMLTextAreaElement>;
  onSend: (message: OutgoingMessage) => void;
}) {
  const [draft, setDraft] = useState("");
  function send(e: FormEvent) {
    e.preventDefault();
    const body = draft.trim();
    if (!body) return;
    setDraft("");
    onSend({ id: crypto.randomUUID(), peer, body });
  }
  return (
    <form className="message-form" onSubmit={send}>
      <textarea
        ref={composerRef}
        aria-label={t("رسالتك")}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          // Enter sends; Shift+Enter and IME composition keep typing.
          if (e.key !== "Enter" || e.shiftKey || e.nativeEvent.isComposing)
            return;
          e.preventDefault();
          e.currentTarget.form?.requestSubmit();
        }}
        placeholder={t("اكتب رسالة…")}
        dir="auto"
        rows={1}
        enterKeyHint="send"
        maxLength={2000}
      />
      <button
        className="button"
        disabled={!draft.trim()}
        aria-label={t("إرسال الرسالة")}
        // Tapping send leaves the keyboard open for the next message.
        onMouseDown={(e) => e.preventDefault()}
      >
        <Send width={18} height={18} />
      </button>
    </form>
  );
}

function Conversation({
  person,
  meId,
  selected,
  onShowChat,
  onOpenPerson,
}: {
  person: Person;
  meId: string;
  selected: boolean;
  onShowChat: (person: Person) => void;
  onOpenPerson: (person: Person) => void;
}) {
  const now = useNow();
  const last = person.last_message;
  const unread = selected ? 0 : (person.unread ?? 0);
  const preview =
    last &&
    (last.sender === meId
      ? t("أنت: {message}", { message: last.body })
      : last.body);
  return (
    <div
      className={`chat-friend ${selected ? "selected" : ""} ${unread ? "unread" : ""}`}
    >
      <ProfileAvatar person={person} onOpen={onOpenPerson} />
      <button
        className="person-name"
        aria-label={t("راسل {name}", { name: person.name })}
        aria-description={
          [
            unread ? t("{count} غير مقروءة", { count: number(unread) }) : "",
            preview,
          ]
            .filter(Boolean)
            .join(". ") || undefined
        }
        aria-current={selected ? "page" : undefined}
        onClick={() => onShowChat(person)}
      >
        <span className="chat-friend-line">
          <strong>{person.name}</strong>
          {last && (
            <time dateTime={new Date(last.created_at).toISOString()}>
              {sameDay(last.created_at, now)
                ? time(last.created_at)
                : day(last.created_at, true)}
            </time>
          )}
        </span>
        <span className="chat-friend-line">
          <small dir={last ? "auto" : undefined}>
            {preview || <ActivityStatus person={person} compact />}
          </small>
          {unread > 0 && (
            <span className="unread-count" aria-hidden="true">
              {number(unread)}
            </span>
          )}
        </span>
      </button>
    </div>
  );
}

// The conversation search takes focus when the Messages icon is chosen again.
const conversationSearch = "conversation-search";
export const focusConversationSearch = () =>
  document.getElementById(conversationSearch)?.focus();

export function MessagesScreen({
  account: { me, friends },
}: {
  account: AuthenticatedSnapshot;
}) {
  const shell = useShell();
  const navigate = useNavigate();
  const client = useQueryClient();
  const { chatId } = useAppLocation();
  const chat = friends.find((friend) => friend.id === chatId) ?? null;
  const meId = me.id;
  const messages = useQuery({
    ...messagesOptions(client, meId, chatId ?? ""),
    enabled: !!chat,
  });
  const send = useSendMessage(),
    discard = useDiscardMessage(),
    { mutate: markRead } = useMarkRead();
  const submitting = useSubmitting();
  const visible = usePageVisible();
  const composer = useRef<HTMLTextAreaElement>(null),
    chatPane = useRef<HTMLElement>(null);
  const openChatId = chat?.id,
    // The count, so a message arriving during the last clear is read too.
    chatUnread = chat?.unread ?? 0;
  useEffect(() => {
    if (openChatId && chatUnread && visible) markRead(openChatId);
  }, [openChatId, chatUnread, visible, markRead]);
  // Keyboard users can type right away; touch screens keep their keyboard closed.
  const focusChat = () =>
    (matchMedia("(pointer: fine)").matches && composer.current
      ? composer.current
      : chatPane.current
    )?.focus({ preventScroll: true });
  const moveFocus = useEffectEvent((closed: boolean) => {
    if (openChatId) focusChat();
    else if (closed) focusConversationSearch();
  });
  const previousChat = useRef(openChatId);
  useEffect(() => {
    moveFocus(!!previousChat.current);
    previousChat.current = openChatId;
  }, [openChatId]);
  const [query, setQuery] = useState("");
  const term = query.trim().replace(/^@/, "").toLowerCase();
  const conversations = friends
    .filter(
      (friend) =>
        friend.name.toLowerCase().includes(term) ||
        friend.username.toLowerCase().includes(term),
    )
    .sort(
      (a, b) =>
        (b.last_message?.created_at ?? 0) - (a.last_message?.created_at ?? 0),
    );
  return (
    <main
      className={`chat-page ${chat ? "has-chat" : ""}`}
      id="main-content"
      tabIndex={-1}
    >
      <aside className="chat-sidebar" aria-label={t("الرسائل")}>
        <h2>{t("الرسائل")}</h2>
        <FriendSearch
          value={query}
          onChange={setQuery}
          inputId={conversationSearch}
          label={t("البحث في المحادثات")}
        />
        <div className="chat-friends">
          {conversations.map((p) => (
            <Conversation
              key={p.id}
              person={p}
              meId={meId}
              selected={p.id === chat?.id}
              onShowChat={shell.showChat}
              onOpenPerson={shell.openPerson}
            />
          ))}
          {!conversations.length && (
            <div className="inline-empty">
              <p>
                {t(
                  friends.length
                    ? "لا يوجد أصدقاء مطابقون."
                    : "لا يوجد أصدقاء بعد.",
                )}
              </p>
              {!friends.length && (
                <button
                  className="quiet-button"
                  onClick={() =>
                    shell.open({ kind: "add-friend", step: "add" })
                  }
                >
                  {t("إضافة صديق")}
                </button>
              )}
            </div>
          )}
        </div>
      </aside>
      {chat ? (
        <section
          className="chat-pane"
          ref={chatPane}
          tabIndex={-1}
          aria-label={t("محادثتك مع {name}", { name: chat.name })}
        >
          <header className="chat-header">
            <button
              className="icon-button chat-back"
              onClick={() => void navigate({ to: "/messages", search: true })}
              aria-label={t("رجوع")}
            >
              <ArrowLeft className="back-directional" width={20} height={20} />
            </button>
            <ProfileAvatar person={chat} onOpen={shell.openPerson} />
            <button
              className="person-name"
              aria-label={t("عرض حساب {name}", { name: chat.name })}
              onClick={() => shell.openPerson(chat)}
            >
              <strong>{chat.name}</strong>
              <small>
                <ActivityStatus person={chat} />
              </small>
            </button>
            <button
              className="icon-button call-action"
              aria-label={t("اتصل بـ{name}", { name: chat.name })}
              onClick={() => shell.callFriend(chat)}
            >
              <Phone width={18} height={18} />
            </button>
            <button
              className="icon-button call-action"
              aria-label={t("مكالمة فيديو مع {name}", { name: chat.name })}
              title={t("مكالمة فيديو")}
              onClick={() => shell.callFriend(chat, "video")}
            >
              <Video width={18} height={18} />
            </button>
            <PersonMenu
              person={chat}
              isFriend
              disabled={submitting}
              onChoose={shell.chooseSafety}
            />
          </header>
          <Fragment key={chat.id}>
            <MessageList
              messages={messages}
              meId={meId}
              peerId={chat.id}
              peerName={chat.name}
              readThrough={chat.read_through ?? 0}
              onRetry={(message) => {
                send.mutate(message);
                focusChat();
              }}
              onDiscard={(message) => {
                discard(message);
                focusChat();
              }}
              onReachLatest={focusChat}
            />
            <MessageForm
              peer={chat.id}
              composerRef={composer}
              onSend={(message) => {
                send.mutate(message);
                composer.current?.focus();
              }}
            />
          </Fragment>
        </section>
      ) : (
        <section className="conversation-empty">
          <MessageCircle width={36} height={36} />
          <h1>{t("اختر محادثة")}</h1>
        </section>
      )}
    </main>
  );
}
